import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Section, Container, PlayPauseButton } from 'components/shared';
import styled from 'styled-components';
import breakpoints from 'utils/breakpoints';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { scrollToSection } from 'utils/scrollTo';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import getEmbedUrl from 'utils/getEmbedUrl';
import getVideoType from 'utils/getVideoType';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import pageTypes from 'utils/pageTypes';
import { useUiValue, setHasCommingSonnCommunity } from 'stores/uiContext';
import { Helmet } from '@dr.pogodin/react-helmet';

import loadable from '@loadable/component';
const Heading = loadable(() => import('components/components/atoms/Heading'));
const Copy = loadable(() => import('components/components/atoms/Copy'));
const IconButton = loadable(() =>
  import('components/components/atoms/IconButton'),
);

const StyledSection = styled(Section)`
  min-height: 34rem;
  height: 87vh;
  background-image: url(${(props) => props.background});
  @media ${breakpoints.md} {
    height: 84vh;
  }
`;

const StyledContainer = styled(Container)`
  min-height: 29rem;
`;

const StyledHeading = styled(Heading)`
  @media ${breakpoints.md} {
  }
`;

const StyledCopy = styled(Copy)`
  @media ${breakpoints.md} {
    width: 40rem;
  }
`;

const Video = styled.video`
  min-height: 87vh;
  @media ${breakpoints.xs} {
    min-height: 90vh;
  }
  @media ${breakpoints.md} {
    min-height: 84vh;
  }
  top: 50%;
  left: 50%;
`;

const GradientTop = styled.div`
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
`;

const CommunityStatusFlag = styled.div`
  border-radius: 0.813rem;
  width: fit-content;
`;

const getScrollToSection = (label) => {
  if (label === 'extended-gallery') return 'gallery';
  if (label === 'IconBlock') return 'whatsnearby';
  if (label === 'InteractiveSitemap') return 'sitemap';
  if (label === 'QMIBlock') return 'availablehomes';
  return label;
};

const ContentHero = ({ fields }) => {
  const currentPageType = getFieldValue(fields, 'currentPageType');
  const hideherooverlay = getFieldValue(fields, 'hideherooverlay');
  const displayOption = getFieldValue(fields, 'displayOption');
  const image = getFieldValue(fields, 'image') || {};
  const video =
    (displayOption === 'Video' && getFieldValue(fields, 'video')) || {};
  const title = getFieldValue(fields, 'title');
  const subtitle = getFieldValue(fields, 'subtitle');
  const communityStatus = fields.communityStatus;
  const scrollToSectionField = getScrollToSection(
    getFieldValue(fields, 'scrollToSection'),
  );
  const [paused, setPaused] = useState(true);
  const isWinOS =
    typeof window !== 'undefined' && window.navigator.platform === 'Win32';
  const btnPlayPauseClass =
    'absolute mb-4 right-0 bottom-0 ' + (isWinOS ? 'mr-8' : 'mr-4');

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const background =
    (isMobile ? image.srcMobile : image.srcDesktop) || image.src;

  const videoRef = useRef(null);

  const [{}, dispatch] = useUiValue();

  const handleUserKeyPress = (event) => {
    const { keyCode } = event;
    if (keyCode === 32) {
      if (videoRef && videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
          setPaused(false);
        } else {
          videoRef.current.pause();
          setPaused(true);
        }
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (communityStatus && communityStatus.toUpperCase() === 'COMING SOON') {
        dispatch(setHasCommingSonnCommunity(true));
      }
    });
  }, [communityStatus]);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  useEffect(() => {
    if (videoRef && videoRef.current) {
      setTimeout(() => {
        videoRef.current.play();
        setPaused(false);
      });
    }
  }, [videoRef]);

  const handlePlayClick = useCallback(() => {
    if (videoRef && videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setPaused(false);
      } else {
        videoRef.current.pause();
        setPaused(true);
      }
    }
  }, [videoRef]);

  const videoType = getVideoType(video.href);

  const videoTranscriptBackgroundColor =
    getFieldValue(fields, 'videoTranscriptBackgroundColor') || 'bg-white';
  const videoTrancriptText = getFieldValue(fields, 'videoTrancriptText');
  const videoTranscriptFile =
    getFieldValue(fields, 'videoTranscriptFile') || {};
  //const elongatedImage = fields.elongatedImage;

  return (
    <>
      {video && video.href && videoType === 'internal' && (
        <Helmet>
          <link
            fetchPriority='high'
            rel='preload'
            as='video'
            href={video.href}
          />
        </Helmet>
      )}
      {background && (
        <Helmet>
          <link
            fetchPriority='high'
            rel='preload'
            as='image'
            href={background}
          />
        </Helmet>
      )}
      <StyledSection
        id='HeaderPlanPage'
        tabIndex={'-1'}
        background={background}
        className='lg:items-start relative bg-center bg-cover
        bg-no-repeat overflow-hidden pb-12 z-15 flex'
      >
        {!isMobile && video && videoType === 'external' && (
          <Video
            as='iframe'
            className='absolute w-full object-cover transform -translate-y-1/2 -translate-x-1/2 bg-black'
            src={
              getEmbedUrl(video.href) + '&version=3&enablejsapi=1&controls=0'
            }
          />
        )}
        {!isMobile && video && videoType === 'internal' && (
          <>
            <Video
              className='absolute w-full object-cover transform -translate-y-1/2 -translate-x-1/2'
              autoPlay
              muted
              loop
              src={video.href}
              ref={videoRef}
              playsInline
            />
          </>
        )}
        <GradientTop className='absolute top-0 bottom-0 w-full' />

        <StyledContainer className='h-full flex flex-1 flex-col items-center justify-center lg:w-9/12 md:px-1/16'>
          {!(currentPageType === pageTypes.CondoPage && hideherooverlay) && (
            <>
              {communityStatus && (
                <CommunityStatusFlag
                  className='flex items-center justify-center
                  px-6 py-1 mt-10-mattamy mb-4 md:mt-0 bg-mattamy-blue text-white'
                >
                  <p className='text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1'>
                    {communityStatus}
                  </p>
                </CommunityStatusFlag>
              )}

              <StyledHeading
                color='white'
                className='leading-10 text-center'
                shadowed
                large={!isMobile}
              >
                {title}
              </StyledHeading>

              <StyledCopy
                color='white'
                shadowed
                className='text-xl text-center -mt-3 leading-7'
              >
                {subtitle}
              </StyledCopy>
            </>
          )}

          {scrollToSectionField && scrollToSectionField.length > 0 && (
            <IconButton
              className='absolute left-0 right-0 m-auto bottom-0 -mb-4 md:mb-6 z-30'
              icon='arrow-down'
              idIcon='ContentHero'
              circled
              shadowed
              color='white'
              onClick={() => {
                scrollToSection(scrollToSectionField);
              }}
              role='link'
              buttonLabel={'Navigate to more information on this page.'}
              id={'productHeroScroll'}
            />
          )}
        </StyledContainer>
        {video && video.href && (
          <PlayPauseButton
            onClick={handlePlayClick}
            paused={paused}
            className={btnPlayPauseClass}
          />
        )}
      </StyledSection>
      {!isMobile &&
        video &&
        videoTranscriptFile &&
        videoTranscriptFile.link &&
        videoTranscriptFile.link.href && (
          <div className={videoTranscriptBackgroundColor}>
            <span className='richtext text-left text-md leading-7 font-graphie text-mattamy-blue mt-2'>
              {'Transcript: '}
              <a
                href={videoTranscriptFileUrl(videoTranscriptFile)}
                download={
                  videoTranscriptFile.name +
                  '.' +
                  videoTranscriptFileExtension(videoTranscriptFile.extension)
                }
                className='underline'
                aria-label={'Download ' + videoTrancriptText + ' transcript'}
              >
                {videoTrancriptText}
              </a>
            </span>
          </div>
        )}
    </>
  );
};

export default ContentHero;
